import { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartOptions,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import Card from 'antd/lib/card';
import { MetricDataResults } from 'aws-sdk/clients/cloudwatch';
import moment from 'moment';
import { blue, green, red, yellow } from '@ant-design/colors';
import { orange } from '@ant-design/colors';
import { cyan } from '@ant-design/colors';
import { magenta } from '@ant-design/colors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const options: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        boxWidth: 12
      }
    },
    tooltip: {
      callbacks: {
        footer: function (items) {
          return 'Total: ' + items.reduce((a, b) => a + b.parsed.y, 0);
        }
      }
    }
  },
  scales: {
    yAxes: {
      stacked: true,
      beginAtZero: true,
      ticks: {
        callback: function (value) {
          if (typeof value === 'number' && value % 1 === 0) {
            return value;
          }
        }
      },
      grid: {
        display: false
      }
    },
    xAxes: {
      grid: {
        display: false
      }
    }
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false
  },
  layout: {
    padding: 10
  }
};

const getLineColorByDataLabel = (
  label: string | undefined
): string | undefined => {
  if (!label) {
    return '#000000';
  }

  if (label.endsWith('-byStatus')) {
    switch (label.split(' ')[0]) {
      case undefined:
        return '#555555';
      case 'SUCCESS':
        return green[3];
      case 'FAILURE':
        return red[3];
      case 'RECEIVED':
        return blue.primary;
    }
  }

  if (label.endsWith('-byChannel') || label.endsWith('-bySuccessChannels')) {
    switch (label.split(' ')[0]) {
      case 'EMAIL':
        return blue[2];
      case 'INAPP_WEB':
        return yellow[2];
      case 'SMS':
        return orange[5];
      case 'CALL':
        return red[4];
      case 'PUSH':
        return cyan[5];
      case 'WEB_PUSH':
        return magenta[5];
    }
  }

  // for graphs with notifications, generate random colors based on the notificationId
  if (
    label.endsWith('-byNotificationId') ||
    label.endsWith('-byStatusAndNotificationId')
  ) {
    return stringToColor(label.split(' ')[0]);
  }

  throw new Error(`Cannot find line color for ${label}`);
};

const stringToColor = (
  string: string,
  saturation = 100,
  lightness = 60
): string => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  return `hsl(${hash % 360}, ${saturation}%, ${lightness}%)`;
};

const getLineLabelbyDataLabel = (label: string | undefined) => {
  // labels have this structure:
  // {dimension1} {dimension2?} {envId}-{metricName}
  // example with 1 dimension: "my-notification 123123-byNotificationId"
  // example with 2 dimension: "my-notification SUCCESS 123123-byStatusAndNotificationId"

  if (!label) return '';

  return label.split(' ')[0];
};

const getCardTitleByGraphId = (graphId: string): string => {
  switch (graphId) {
    case 'byStatus':
      return 'Notification Status';
    case 'byChannel':
      return 'Outgoing Channels';
    case 'byNotificationId':
      return 'Outgoing Notifications';
    case 'byStatusAndNotificationId':
      return 'Failures by Notification';
    case 'bySuccessChannels':
      return 'Failures by Channel';
  }

  throw new Error(`Cannot find graph title for ${graphId}`);
};

interface Props {
  id: string;
  data: MetricDataResults;
  dateRange: [moment.Moment, moment.Moment];
  detail: number;
}

const Graph = ({ id, data, dateRange, detail }: Props): JSX.Element => {
  const [chartData, setChartData] = useState({
    type: 'line',
    datasets: [
      {
        data: [{ x: '', y: 0 }]
      }
    ]
  });

  useEffect(() => {
    // create xAxis labels
    const xAxisLength = Math.round(
      (dateRange[1].unix() - dateRange[0].unix()) / detail
    );
    const xAxisLabels: string[] = [];
    const xAxisValues: moment.Moment[] = [];
    for (let i = 0; i <= xAxisLength; i++) {
      xAxisValues[i] = moment(dateRange[0])
        .add(i * detail, 's')
        .set('h', 2) // AWS returns the dates set at 2am, we match our labels with that for accuracy
        .utc();
      xAxisLabels[i] =
        xAxisValues[i].get('M') + 1 + '-' + xAxisValues[i].get('D');
    }

    setChartData({
      type: 'line',
      datasets: data.map((d) => {
        // each dataset array and each data array element represent one line
        return {
          data: xAxisLabels.map((xAxisLabel, i) => {
            // for each date on xAxisLabel, see if any data in d matches the xAxisValues
            const foundAtIndex = d.Timestamps?.findIndex((timestamp) => {
              return moment(timestamp).utc().isSame(xAxisValues[i], 'day');
            });

            // if found, use the value, otherwise set to 0 for a nicer graph
            let value = 0;
            if (foundAtIndex !== undefined && foundAtIndex > -1) {
              value = d.Values ? d.Values[foundAtIndex] : 0;
            }

            return {
              x: xAxisLabel,
              y: value
            };
          }),
          label: getLineLabelbyDataLabel(d.Label),
          borderColor: getLineColorByDataLabel(d.Label),
          backgroundColor: getLineColorByDataLabel(d.Label),
          pointHitRadius: 5,
          tension: 0,
          borderWidth: 0,
          pointRadius: 0,
          fill: true
        };
      })
    });
  }, [data, dateRange, detail]);

  return (
    <Card title={getCardTitleByGraphId(id)} style={{ marginBottom: '20px' }}>
      <div style={{ height: '300px' }}>
        <Line options={options} data={chartData} />
      </div>
    </Card>
  );
};

export default Graph;
